import React, { useState } from 'react';

const getRatingColor = (rating) => {
  if (rating >= 8) return { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' };
  if (rating >= 6) return { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' };
  if (rating >= 4) return { bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300' };
  return { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' };
};

const getRatingLabel = (rating) => {
  if (rating >= 8) return 'Strong';
  if (rating >= 6) return 'Good';
  if (rating >= 4) return 'Concerns';
  return 'Blockers';
};

const SIZES = {
  sm: { text: 'text-[10px] font-bold', pill: 'px-1.5 py-0.5' },
  md: { text: 'text-xs font-bold', pill: 'px-2 py-0.5' },
  lg: { text: 'text-sm font-bold', pill: 'px-2.5 py-1' },
};

const ProposalRatingBadge = ({ rating, reasons, size = 'md', showPopup = false }) => {
  const [popupOpen, setPopupOpen] = useState(false);

  if (rating == null) return null;

  const colors = getRatingColor(rating);
  const sizeConfig = SIZES[size] || SIZES.md;

  return (
    <div className="relative inline-flex">
      <button
        onClick={(e) => {
          e.stopPropagation();
          if (showPopup) setPopupOpen(!popupOpen);
        }}
        className={`inline-flex items-center gap-1 ${sizeConfig.pill} ${colors.bg} ${colors.border} ${colors.text} border rounded-full ${showPopup ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
        title={`AI Expert Analysis: ${rating}/10 — ${getRatingLabel(rating)}`}
      >
        <svg className={`${size === 'sm' ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
        </svg>
        <span className={sizeConfig.text}>{rating}/10</span>
      </button>

      {/* Popup overlay */}
      {showPopup && popupOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setPopupOpen(false)} />
          <div className="absolute right-0 top-full mt-2 z-50 w-96 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-100">
              <div className="flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">AI Expert Analysis</h4>
                <span className={`${colors.bg} ${colors.text} ${colors.border} border text-xs font-bold px-2 py-0.5 rounded-full`}>
                  {rating}/10 — {getRatingLabel(rating)}
                </span>
              </div>
            </div>
            {reasons && reasons.length > 0 ? (
              <div className="px-4 py-3 space-y-2 max-h-64 overflow-y-auto">
                {reasons.map((reason, idx) => (
                  <div
                    key={idx}
                    className={`rounded-lg border px-3 py-2 flex items-start gap-2 ${
                      reason.impact === 'negative'
                        ? 'bg-red-50 border-red-200'
                        : reason.impact === 'positive'
                        ? 'bg-green-50 border-green-200'
                        : 'bg-gray-50 border-gray-200'
                    }`}
                  >
                    {reason.impact === 'negative' ? (
                      <svg className="w-3.5 h-3.5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    ) : reason.impact === 'positive' ? (
                      <svg className="w-3.5 h-3.5 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-3.5 h-3.5 text-gray-400 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <div className="min-w-0">
                      <span className={`text-[9px] font-semibold uppercase tracking-wide ${
                        reason.impact === 'negative' ? 'text-red-600' :
                        reason.impact === 'positive' ? 'text-green-600' : 'text-gray-500'
                      }`}>
                        {(reason.category || '').replace(/_/g, ' ')}
                      </span>
                      <p className="text-[11px] text-gray-700 leading-relaxed">{reason.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 text-xs text-gray-400">No analysis details available.</div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProposalRatingBadge;
